import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, finalize, Subject, Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { CreatorKbDialogComponent } from '../../../components/dialogs/creator-kb-dialog/creator-kb-dialog.component';
import { CreatorRenameDialogComponent } from '../../../components/dialogs/creator-rename-dialog/creator-rename-dialog.component';
import { CreatorTrashDialogComponent } from '../../../components/dialogs/creator-trash-dialog/creator-trash-dialog.component';
import { AlTableBadgeComponent } from '../../../components/features/table-badge/table-badge.component';
import { AlTableTextComponent } from '../../../components/features/table-text/table-text.component';
import { AlTableColumn, AlTableRowAction } from '../../../components/features/table/table.component';
import { SegmentOption } from '../../../components/segmented-control/segmented-control.component';
import { AuthService } from '../../../services/auth.service';
import {
  AC_KINDS,
  COURSE_KIND_FILTERS,
  CreatorLibraryService,
  creatorFailure,
  ItemSummary,
  KB_KINDS,
  KIND_FILTERS,
  KindFilter,
} from '../../../services/creator-library.service';
import { createdByOther, foreignLock, liveLabels, relativeTime } from '../creator-library';
import { CreatorOpenCellComponent } from './creator-open-cell.component';

/** One table row: the item plus its derived cells, built once per load (rule 18). */
interface LibraryRow extends ItemSummary {
  kindLabel: string;
  statusLabel: string;
  note: string;
  liveText: string;
  changedText: string;
  mine: boolean;
}

const PAGE_SIZE = 25;

/**
 * Creator › Bibliothek (`/creator/library`): the organisation's content, newest
 * change first, paged on the server (25). Open goes to the workspace (canvas;
 * E-Learning and Microlearning straight into the course editor), the row menu
 * renames content, puts a version into a knowledge base, releases own private content or moves
 * content into the trash (30 days, restorable from „Papierkorb").
 * Tenants without the library never get here: `/creator` sends them to the
 * canvas.
 */
@Component({
  selector: 'app-creator-library',
  templateUrl: './creator-library.component.html',
  styleUrl: './creator-library.component.scss',
})
export class CreatorLibraryComponent implements OnInit, OnDestroy {
  readonly pageSize = PAGE_SIZE;
  readonly pageSizes = [PAGE_SIZE];
  rows: LibraryRow[] = [];
  total = 0;
  page = 0;
  filter: KindFilter = 'all';
  /** Under „Kurse": all kinds of course or one of them. */
  courseKind = 'all';
  /** Title search (server side, debounced). */
  query = '';
  state: 'loading' | 'ready' | 'error' = 'loading';
  busy = false;

  columns: AlTableColumn[] = [];
  rowActions: AlTableRowAction[] = [];
  readonly filters: SegmentOption[];
  readonly courseFilters: SegmentOption[];

  private loadSub?: Subscription;
  private querySub?: Subscription;
  private readonly query$ = new Subject<string>();
  private readonly userId: string | undefined;

  constructor(
    private library: CreatorLibraryService,
    private auth: AuthService,
    private dialogs: NbDialogService,
    private toastr: NbToastrService,
    private router: Router,
    private t: TranslateService,
  ) {
    this.userId = this.auth.accessTokenPayload$.value?.user_id;
    this.filters = (Object.keys(KIND_FILTERS) as KindFilter[]).map((key) => ({
      value: key,
      label: t.instant(`pages.creator.library.filter.${key}`),
    }));
    this.courseFilters = COURSE_KIND_FILTERS.map((key) => ({
      value: key,
      label: t.instant(`pages.creator.library.courseFilter.${key}`),
    }));
  }

  ngOnInit(): void {
    this.buildTable();
    this.querySub = this.query$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.page = 0;
      this.load();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.querySub?.unsubscribe();
  }

  setFilter(value: string): void {
    if (value === this.filter) return;
    this.filter = value as KindFilter;
    this.courseKind = 'all';
    this.page = 0;
    this.load();
  }

  setCourseKind(value: string): void {
    if (value === this.courseKind) return;
    this.courseKind = value;
    this.page = 0;
    this.load();
  }

  onQuery(value: string): void {
    this.query = value;
    this.query$.next(value.trim());
  }

  /** The empty state for the current filter and search. */
  get emptyText(): string {
    if (this.query.trim()) return this.t.instant('pages.creator.library.noMatch', { query: this.query.trim() });
    return this.t.instant(this.filter === 'all' && this.courseKind === 'all' ? 'pages.creator.library.empty' : 'pages.creator.library.filter.empty');
  }

  private kinds(): readonly string[] {
    return this.filter === 'courses' && this.courseKind !== 'all' ? [this.courseKind] : KIND_FILTERS[this.filter];
  }

  setPage(page: number): void {
    this.page = page;
    this.load();
  }

  load(): void {
    this.loadSub?.unsubscribe();
    if (this.state !== 'ready') this.state = 'loading';
    this.busy = true;
    this.loadSub = this.library
      .list(PAGE_SIZE, this.page * PAGE_SIZE, this.kinds(), this.query)
      .pipe(finalize(() => (this.busy = false)))
      .subscribe({
        next: (page) => {
          this.total = page.total;
          this.rows = page.items.map((item) => this.toRow(item));
          this.state = 'ready';
        },
        error: () => {
          // a reload keeps what is shown; only the first load shows the error
          if (this.state !== 'ready') this.state = 'error';
          else this.toastr.danger(this.t.instant('pages.creator.library.loadError'), '');
        },
      });
  }

  openTrash(): void {
    this.dialogs.open(CreatorTrashDialogComponent, { context: { onRestored: () => this.load() } });
  }

  onTableEvent(payload: { event: string; data: any }): void {
    if (payload.event !== 'rowAction') return;
    const row = payload.data?.row as LibraryRow;
    if (!row) return;
    switch (payload.data?.action?.key) {
      case 'open': return this.open(row);
      case 'rename': return this.rename(row);
      case 'kb': return this.publishToKb(row);
      case 'release': return this.release(row);
      case 'delete': return this.confirmDelete(row);
    }
  }

  open(row: ItemSummary): void {
    const queryParams = AC_KINDS.includes(row.kind)
      ? { item: row.id, kind: row.kind, title: row.title }
      : { item: row.id };
    this.router.navigate(['/creator/workspace'], { queryParams });
  }

  private rename(row: LibraryRow): void {
    this.dialogs.open(CreatorRenameDialogComponent, { context: { item: row, onRenamed: () => this.load() } });
  }

  private publishToKb(row: LibraryRow): void {
    this.dialogs.open(CreatorKbDialogComponent, { context: { item: row, onChanged: () => this.load() } });
  }

  private release(row: LibraryRow): void {
    this.library.release(row.id).subscribe({
      next: () => {
        this.toastr.success(this.t.instant('pages.creator.library.released', { title: row.title }), '');
        this.load();
      },
      error: () => this.toastr.danger(this.t.instant('pages.creator.library.releaseFailed'), ''),
    });
  }

  private confirmDelete(row: LibraryRow): void {
    const ref = this.dialogs.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.creator.library.deleteTitle',
        message: this.t.instant('pages.creator.library.deleteMessage', { title: row.title }),
        confirmLabel: 'buttons.delete',
        onConfirm: () => {
          ref.close();
          this.library.remove(row.id).subscribe({
            next: () => {
              this.toastr.success(this.t.instant('pages.creator.library.deleted', { title: row.title }), '');
              this.load();
            },
            error: (err) => {
              const failure = creatorFailure(err);
              const key = failure.code === 'forbidden'
                ? 'pages.creator.library.deleteForbidden'
                : failure.code === 'locked'
                  ? 'pages.creator.library.deleteLocked'
                  : 'pages.creator.library.deleteFailed';
              this.toastr.danger(this.t.instant(key), '');
            },
          });
        },
      },
    });
  }

  private toRow(item: ItemSummary): LibraryRow {
    const now = Date.now();
    const lang = this.t.currentLang || 'de';
    const lock = foreignLock(item, this.userId, now);
    const notes: string[] = [];
    if (lock) notes.push(this.t.instant('pages.creator.library.lockedBy', { name: lock.by.name }));
    if (item.visibility === 'private') notes.push(this.t.instant('pages.creator.library.private'));
    if (item.changedSinceVersion) {
      notes.push(this.t.instant('pages.creator.library.changedSince', { version: item.changedSinceVersion }));
    }
    const author = createdByOther(item);
    if (author) notes.push(this.t.instant('pages.creator.library.createdBy', { name: author }));
    const targets = this.t.instant('pages.creator.targets');
    const live = liveLabels(item.liveIn, typeof targets === 'object' ? targets : {})
      .map((l) => `${l.text}${l.outdated ? ' (!)' : ''}${l.failed ? ' ✕' : ''}`)
      .join(' · ');
    const when = relativeTime(item.updatedAt, now, lang);
    return {
      ...item,
      kindLabel: this.label('pages.creator.kinds', item.kind),
      statusLabel: this.label('pages.creator.status', item.status),
      note: notes.join(' · '),
      liveText: live || '—',
      changedText: item.updatedBy ? `${when} · ${item.updatedBy.name}` : when,
      mine: !!this.userId && item.createdBy?.id === this.userId,
    };
  }

  private label(prefix: string, value: string): string {
    const key = `${prefix}.${value}`;
    const text = this.t.instant(key);
    return text === key ? value : text;
  }

  /** Stable for the open cell (one function, not one per change detection). */
  private readonly openRow = (row: ItemSummary) => this.open(row);

  private buildTable(): void {
    const openLabel = this.t.instant('pages.creator.library.open');
    this.columns = [
      {
        key: 'title',
        kind: 'name',
        title: this.t.instant('pages.creator.library.columns.title'),
        width: '30%',
        component: AlTableTextComponent,
        componentInputs: ({ row }) => ({ text: row.title, max: 120, suffix: row.note || undefined }),
      },
      { key: 'kindLabel', title: this.t.instant('pages.creator.library.columns.kind'), width: '12%' },
      {
        key: 'status',
        title: this.t.instant('pages.creator.library.columns.status'),
        width: '10%',
        component: AlTableBadgeComponent,
        componentInputs: ({ row }) => ({ label: row.statusLabel, variant: row.status === 'final' ? 'success' : 'muted' }),
      },
      { key: 'liveText', kind: 'text', title: this.t.instant('pages.creator.library.columns.liveIn'), width: '14%' },
      { key: 'changedText', title: this.t.instant('pages.creator.library.columns.changed'), width: '24%' },
      {
        key: 'openCell',
        title: '',
        width: '8%',
        component: CreatorOpenCellComponent,
        componentInputs: ({ row }) => ({ row, label: openLabel, open: this.openRow }),
      },
    ];
    this.rowActions = [
      // „Öffnen" is the button in the row; the menu holds the other actions
      { key: 'rename', title: this.t.instant('pages.creator.library.rename'), icon: 'edit-2-outline' },
      {
        key: 'kb',
        title: this.t.instant('pages.creator.library.kb'),
        icon: 'hard-drive-outline',
        disabled: (row: LibraryRow) => !KB_KINDS.includes(row.kind),
        tooltip: (row: LibraryRow) => (KB_KINDS.includes(row.kind) ? '' : this.t.instant('pages.creator.library.kbNotYet')),
      },
      {
        key: 'release',
        title: this.t.instant('pages.creator.library.release'),
        icon: 'people-outline',
        disabled: (row: LibraryRow) => !(row.visibility === 'private' && row.mine),
        tooltip: () => this.t.instant('pages.creator.library.releaseHint'),
      },
      { key: 'delete', title: this.t.instant('buttons.delete'), icon: 'trash-2-outline' },
    ] as AlTableRowAction[];
  }
}
